<template>
    <div class="lsva-truck-details">
        <div class="lsva-truck-details__head">
            <h3>{{ $t('heading') }}</h3>
            <IconButton @click="$emit('close')">
                <RemoveIcon width="24" height="24" />
            </IconButton>
        </div>
        <div v-if="isEditing && selectedTruck" class="l-padded">
            <LsvaTruckEdit
                :truck="selectedTruck.toll_declaration.truck"
                @close="toggleEditTruckForm"
            />
        </div>
        <div v-else class="lsva-truck-details__info">
            <div class="lsva-truck-details__icon">
                <BaseAvatar
                    :src="selectedTruck.toll_declaration.truck.marker"
                    :color="selectedTruck.toll_declaration.truck.color"
                    :size="64"
                />
            </div>
            <div class="lsva-truck-details__data l-grow-1 l-stack l-gap-1">
                <h3>{{ selectedTruck.toll_declaration.truck.name }}</h3>
                <p>VIN: {{ selectedTruck.toll_declaration.truck.vin }}</p>
                <p>
                    {{ $t('shared.lsva.columns.endOfRegistration') }}:
                    {{ formattedEndDate }}
                </p>
            </div>
            <div class="lsva-truck-details__edit">
                <IconButton @click="toggleEditTruckForm">
                    <PencilIcon width="16" height="16" />
                </IconButton>
            </div>
        </div>
        <div class="l-padded-x">
            <BaseAlert v-if="showAlert" :variant="manual_correction_variant">
                <div class="l-stack l-gap-2">
                    <div>
                        {{
                            $t(
                                `correctionCauses.${selectedTruck.needs_manual_correction_cause}`
                            )
                        }}
                    </div>
                    <div class="l-inline l-gap-1">
                        <BaseButton
                            v-if="selectedTruck.needs_manual_correction"
                            size="small"
                            variant="danger"
                            :disabled="isErrorAcknowledgementPending"
                            @click="acknowledgeError"
                        >
                            {{ $t('acknowledgeError') }}
                        </BaseButton>
                        <BaseButton
                            v-if="
                                !isTripsLoading &&
                                    !hasNoJourney &&
                                    !needsGpsGapCorrection
                            "
                            size="small"
                            :variant="
                                selectedTruck.needs_manual_correction
                                    ? 'danger'
                                    : 'solid'
                            "
                            @click="addJourney"
                        >
                            {{ $t('noJourneys') }}
                        </BaseButton>
                    </div>
                </div>
            </BaseAlert>
        </div>
        <div class="lsva-truck-details__info">
            <div
                class="lsva-truck-details__data lsva-truck-details__data--accent"
            >
                {{ formattedDate }}
            </div>
            <BaseButton size="small" @click="handleCreateTripClick">
                {{ $t('addTrip') }}
            </BaseButton>
        </div>
        <div class="lsva-truck-details__trips">
            <VSpinner
                v-if="isTripsLoading"
                :size="24"
                :speed="1"
                line-fg-color="black"
                line-bg-color="transparent"
                class="lsva-truck-details__trips-spinner"
            />
            <template v-else-if="tripsAndGaps && tripsAndGaps.length">
                <div
                    class="lsva-truck-details__trips-head l-inline l-justify-end l-padded"
                >
                    <div
                        class="lsva-truck-details__trips-select-all l-inline l-center"
                    >
                        <input
                            type="checkbox"
                            :checked="isAllTripsSelected"
                            @change="handleSelectAllTrips"
                        />
                    </div>
                </div>
                <LsvaTrip
                    v-for="trip in tripsAndGaps"
                    :key="trip.id"
                    :trip="trip"
                    :has-error="
                        selectedTruck.needs_manual_correction &&
                            selectedTruck.needs_manual_correction_cause !==
                                'GPS_ISSUE'
                    "
                    :error-text="selectedTruckErrorText"
                    @gap-filled="handleGapFilled"
                />
            </template>
            <template v-else>
                <div class="l-padded">
                    <BaseAlert variant="info">
                        {{ $t('noJourneys') }}
                    </BaseAlert>
                </div>
            </template>
            <div v-if="isCreateTripVisible" class="l-padded">
                <LsvaTripCreate
                    ref="createTripForm"
                    :title="$t('addTrip')"
                    @close="handleCloseCreateTrip"
                    @create="handleCloseCreateTrip"
                />
            </div>
            <form
                v-if="selectedTrips.length > 0"
                class="lsva-truck-details__bulk-edit-form l-padded l-stack l-gap-3"
                @submit.prevent="submitBulkEditTrips"
            >
                <div class="l-stack l-gap-2">
                    <BaseMultiselect
                        v-model="bulkEditForm.trailer"
                        :options="trailersExtended"
                        track-by="id"
                        label="name"
                        :placeholder="$t('trailer')"
                        :loading="isTrailersLoading"
                        :disabled="isBulkEditPending"
                        @input="handleTrailerSelected"
                    />

                    <template
                        v-if="
                            bulkEditForm.trailer &&
                                bulkEditForm.trailer.id !== 'NO_TRAILER'
                        "
                    >
                        <BaseMultiselect
                            v-model="bulkEditForm.trailerType"
                            :options="trailerTypes"
                            :custom-label="
                                option =>
                                    $t(`shared.lsva.trailerTypes.${option}`)
                            "
                            :placeholder="$t('trailerType')"
                            :loading="isTrailersLoading"
                            :disabled="
                                (bulkEditForm.trailer &&
                                    bulkEditForm.trailer.id !==
                                        'OTHER_TRAILER') ||
                                    isBulkEditPending
                            "
                            :is-error="$v.bulkEditForm.trailerType.$error"
                            :error-text="trailerTypeError"
                        />

                        <BaseInput
                            v-model="bulkEditForm.weight"
                            :placeholder="$t('weight')"
                            :disabled="
                                (bulkEditForm.trailer &&
                                    bulkEditForm.trailer.id !==
                                        'OTHER_TRAILER') ||
                                    isBulkEditPending
                            "
                            :is-error="$v.bulkEditForm.weight.$error"
                            :error-text="weightError"
                            type="number"
                            step="0.01"
                        >
                            <template #icon>
                                <ScalesIcon width="16" height="16" />
                            </template>
                            <template #postfix>
                                {{ $t('shared.units.tons') }}
                            </template>
                        </BaseInput>
                    </template>

                    <BaseInput
                        v-model="bulkEditForm.comment"
                        :placeholder="$t('comment')"
                        :disabled="isBulkEditPending"
                        textarea
                    />
                </div>

                <div class="l-inline l-spread">
                    <BaseButton
                        v-if="hasItemsToDelete"
                        size="small"
                        variant="danger"
                        :is-loading="isRevokePending"
                        @click="deleteSelected"
                    >
                        {{ $t('deleteSelected') }}
                    </BaseButton>
                    <BaseButton
                        type="submit"
                        size="small"
                        :is-loading="isBulkEditPending"
                        :disabled="!bulkEditForm.trailer"
                    >
                        {{ $t('shared.save') }}
                    </BaseButton>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import VSpinner from 'vue-simple-spinner'
import { mapState, mapGetters, mapActions } from 'vuex'
import { requiredIf } from 'vuelidate/lib/validators'

import { TRAILER_TYPES } from '@/constants'
import { httpHelper } from '@/utils'
import BaseAlert from '@/components/base/BaseAlert'
import BaseAvatar from '@/components/base/BaseAvatar'
import BaseButton from '@/components/redesigned/BaseButton'
import BaseInput from '@/components/redesigned/BaseInput'
import BaseMultiselect from '@/components/redesigned/BaseMultiselect'
import IconButton from '@/components/IconButton'
import LsvaTrip from '@/components/lsva/LsvaTrip'
import LsvaTripCreate from '@/components/lsva/LsvaTripCreate'
import LsvaTruckEdit from '@/components/lsva/LsvaTruckEdit'
import PencilIcon from '@/components/icons/PencilIcon'
import RemoveIcon from '@/components/icons/RemoveIcon'
import ScalesIcon from '@/components/icons/ScalesIcon'

const bulkEditFormInitialValues = {
    comment: '',
    trailer: null,
    trailerType: null,
    weight: '',
}

export default {
    name: 'LsvaTruckDetailsView',
    components: {
        BaseAlert,
        BaseAvatar,
        BaseButton,
        BaseInput,
        BaseMultiselect,
        IconButton,
        LsvaTrip,
        LsvaTripCreate,
        LsvaTruckEdit,
        PencilIcon,
        RemoveIcon,
        ScalesIcon,
        VSpinner,
    },
    data() {
        return {
            bulkEditForm: { ...bulkEditFormInitialValues },
            isCreateTripVisible: false,
            isEditing: false,
            trailerTypes: TRAILER_TYPES,
        }
    },
    computed: {
        ...mapState('lsva', [
            'isBulkEditPending',
            'isRevokePending',
            'isErrorAcknowledgementPending',
            'isTrailersLoading',
            'isTripsLoading',
            'selectedDay',
            'selectedTrips',
            'selectedTruck',
            'trailers',
        ]),
        ...mapGetters('lsva', ['preparedTrips', 'hasNoJourney', 'gpsGaps']),
        formattedDate() {
            const selectedDate = moment(this.selectedDay.date)
            return selectedDate
                .format('LLLL')
                .replace(selectedDate.format('LT'), '')
                .replace(selectedDate.format('YYYY'), '')
                .replace(/,\s*$/, '')
                .trim()
        },
        trailersExtended() {
            return [
                { id: 'NO_TRAILER', name: this.$t('noTrailer') },
                { id: 'OTHER_TRAILER', name: this.$t('otherTrailer') },
                ...this.trailers,
            ]
        },
        selectedTruckErrorText() {
            if (
                this.selectedTruck.needs_manual_correction_cause === 'GPS_ISSUE'
            ) {
                return ''
            }
            if (
                this.selectedTruck.declaration_type === 'GNSS' &&
                this.selectedTruck.gnss_without_trip
            ) {
                return 'GNSS_WITHOUT_TRIP'
            } else if (
                this.selectedTruck.declaration_type === 'GNSS' &&
                !this.selectedTruck.gnss_without_trip
            ) {
                return 'GNSS_WITH_TRIP'
            }

            return this.selectedTruck.declaration_type
        },
        isAllTripsSelected() {
            return this.visiblePreparedTrips.some(trip =>
                this.selectedTrips.includes(trip)
            )
        },
        visiblePreparedTrips() {
            return this.preparedTrips.filter(
                trip => !trip?.hidden && !trip?.toll_declaration?.hidden
            )
        },
        formattedEndDate() {
            return this.selectedTruck.toll_declaration.truck.end_date
                ? moment(
                      this.selectedTruck.toll_declaration.truck.end_date
                  ).format('DD.MM.YYYY')
                : this.$t('noEnd')
        },
        trailerTypeError() {
            if (!this.$v.bulkEditForm.trailerType.$dirty) return ''
            if (!this.$v.bulkEditForm.trailerType.required)
                return this.$t('errors.trailerTypeRequired')
            return ''
        },
        weightError() {
            if (!this.$v.bulkEditForm.weight.$dirty) return ''
            if (!this.$v.bulkEditForm.weight.required)
                return this.$t('errors.weightRequired')
            return ''
        },
        hasItemsToDelete() {
            return this.selectedTrips.some(
                item =>
                    item.toll_declaration?.declaration_period &&
                    item.toll_declaration?.toll_declaration_id
            )
        },
        needsGpsGapCorrection() {
            return (
                this.selectedTruck.needs_manual_correction &&
                this.selectedTruck.needs_manual_correction_cause === 'GPS_ISSUE'
            )
        },
        tripsAndGaps() {
            if (this.visiblePreparedTrips.length === 1) {
                const { distance, start, end } = this.visiblePreparedTrips[0]
                if (distance === 0 && moment(start).isSame(moment(end))) {
                    return []
                }
            }
            return [
                ...this.visiblePreparedTrips,
                ...(this.needsGpsGapCorrection ? this.gpsGaps : []),
            ].sort((a, b) => new Date(a.sortingDate) - new Date(b.sortingDate))
        },
        manual_correction_variant() {
            if (this.needsGpsGapCorrection) return 'warning'
            if (this.selectedTruck.needs_manual_correction) return 'danger'
            return 'primary'
        },
        showAlert() {
            return (
                this.needsGpsGapCorrection ||
                (this.selectedTruck.needs_manual_correction_cause &&
                    this.selectedTruck.needs_manual_correction_cause !==
                        'GPS_ISSUE')
            )
        },
    },
    watch: {
        selectedTruck() {
            this.isCreateTripVisible = false
        },
    },
    mounted() {
        if (this._gapsUnwatch) this._gapsUnwatch()
        this._gapsUnwatch = this.$watch(
            () => [
                this.gpsGaps.length,
                this.selectedTruck?.toll_declaration?.toll_declaration_id,
            ],
            async (
                [newNumGaps, newDeclaration],
                [oldNumGaps, oldDeclaration]
            ) => {
                if (
                    newDeclaration === oldDeclaration &&
                    this.needsGpsGapCorrection &&
                    newNumGaps === 0 &&
                    oldNumGaps !== 0
                ) {
                    await this.sendErrorAcknowledgement()
                }
            }
        )
    },
    validations: {
        bulkEditForm: {
            trailer: {
                required: requiredIf(vm => vm.type !== 'JOURNEY'),
            },
            trailerType: {
                required: requiredIf(
                    vm =>
                        vm.type !== 'JOURNEY' &&
                        vm.trailer?.id === 'OTHER_TRAILER'
                ),
            },
            weight: {
                required: requiredIf(
                    vm =>
                        vm.type !== 'JOURNEY' &&
                        vm.trailer?.id === 'OTHER_TRAILER'
                ),
            },
        },
    },
    methods: {
        ...mapActions('lsva', [
            'createCorrection',
            'sendErrorAcknowledgement',
            'selectAllTrips',
            'bulkEditTrips',
            'revokeSelectedDeclarations',
        ]),
        handleTrailerSelected() {
            if (
                !['OTHER_TRAILER', 'NO_TRAILER'].includes(
                    this.bulkEditForm.trailer?.id
                )
            ) {
                this.bulkEditForm.trailerType = this.bulkEditForm.trailer.type
                this.$v.bulkEditForm.trailerType.$reset()
                this.bulkEditForm.weight =
                    this.bulkEditForm.trailer.weight / 1000
                this.$v.bulkEditForm.weight.$reset()
            }
        },
        toggleEditTruckForm() {
            this.isEditing = !this.isEditing
        },
        handleCreateTripClick() {
            this.isCreateTripVisible = true
            this.$nextTick(() => {
                this.$refs.createTripForm.$el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
            })
        },
        handleCloseCreateTrip() {
            this.isCreateTripVisible = false
        },
        handleGapFilled() {
            // should trigger before trips and gaps are refreshed
            if (this.gpsGaps.length === 1) {
                this.sendErrorAcknowledgement()
            }
        },
        acknowledgeError() {
            const notification = {
                title: this.$t('shared.success'),
                text: this.$t('errorAcknowledged'),
            }
            const errorTitle = this.$t('shared.error')

            this.$modal.show('dialog', {
                title: this.$t('acknowledgeError'),
                text: this.$t('acknowledgeErrorText'),
                buttons: [
                    {
                        title: this.$t('confirm'),
                        handler: async () => {
                            this.$modal.hide('dialog')
                            try {
                                await this.sendErrorAcknowledgement()
                                this.$notify(notification)
                            } catch (error) {
                                this.$notify({
                                    title: errorTitle,
                                    text: httpHelper.parseError(error),
                                })
                            }
                        },
                    },
                    {
                        title: this.$t('cancel'),
                    },
                ],
            })
        },
        async addJourney() {
            const notification = {
                title: this.$t('shared.success'),
                text: this.$t('correctionCreated'),
            }

            try {
                this.createCorrection({
                    type: 'JOURNEY',
                    reason: 'NO_JOURNEY',
                    time: ['00:00:00', '23:59:59'],
                })

                this.$notify(notification)
            } catch (error) {
                this.$notify({
                    title: this.$t('shared.error'),
                    text: httpHelper.parseError(error),
                })
            }
        },
        handleSelectAllTrips(e) {
            if (e.target.checked) {
                this.bulkEditForm = { ...bulkEditFormInitialValues }
                this.selectAllTrips(this.visiblePreparedTrips)
            } else {
                this.selectAllTrips([])
            }
        },
        async submitBulkEditTrips() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                return
            }
            const notification = {
                title: this.$t('shared.success'),
                text: this.$t('correctionsCreated'),
            }
            try {
                await this.bulkEditTrips(this.bulkEditForm)
                this.$notify(notification)
            } catch (error) {
                this.$notify({
                    title: this.$t('shared.error'),
                    text: httpHelper.parseError(error),
                })
            } finally {
                this.bulkEditForm = { ...bulkEditFormInitialValues }
            }
        },
        async deleteSelected() {
            const notification = {
                title: this.$t('shared.success'),
                text: this.$t('tripRevoked'),
            }
            const errorTitle = this.$t('shared.error')

            this.$modal.show('dialog', {
                title: this.$t('warning'),
                text: this.$t('confirmTripsRevocation'),
                buttons: [
                    {
                        title: this.$t('shared.yes'),
                        handler: async () => {
                            this.$modal.hide('dialog')
                            try {
                                await this.revokeSelectedDeclarations()
                                this.$notify(notification)
                            } catch (error) {
                                this.$notify({
                                    title: errorTitle,
                                    text: httpHelper.parseError(error),
                                })
                            }
                        },
                    },
                    {
                        title: this.$t('shared.no'),
                    },
                ],
            })
        },
    },
}
</script>

<i18n>
{
    "en": {
        "addTrip": "Add trip",
        "noJourneys": "No journeys",
        "acknowledgeError": "Acknowledge error",
        "acknowledgeErrorText": "Please confirm that you have acknowledged the tracking error and have recorded any LSV-required trips.",
        "comment": "Comment",
        "correctionCreated": "Correction created",
        "correctionsCreated": "Corrections created",
        "confirm": "Confirm",
        "cancel": "Cancel",
        "deleteSelected": "Delete selected",
        "warning": "Warning!",
        "confirmTripsRevocation": "Do you really want to revoke selected trips?",
        "errorAcknowledged": "Error acknowledged",
        "heading": "Edit",
        "noEnd": "No end",
        "noTrackingWarningOptional": "The signal to the tracker was not found. No trips were tracked. You can optionally record trips.",
        "trailer": "Trailer",
        "trailerType": "Trailer type",
        "noTrailer": "No trailer",
        "otherTrailer": "Other trailer",
        "trip": "Trip",
        "weight": "Weight",
        "tripRevoked": "Trip revoked",
        "correctionCauses": {
            "CONNECTION_LOST": "No messages were received throughout the day. Therefore, trips must be entered manually.",
            "NOT_INSTALLED": "The recording device has not yet been put into operation. Therefore, journeys must be added manually.",
            "INSTALLATION_DATE": "The recording device was put into operation on this day. Therefore, any trips before the start of operation must be added manually.",
            "CONNECTION_LOST_ON_NEXT_DAY": "The recording device potentially lost connection during the day. Therefore, any trips must be added manually.",
            "GPS_ISSUE": "The GPS signal was partially unreliable during the day. Therefore, trips must be manually checked and possibly added."
        },
        "errors": {
            "trailerTypeRequired": "Trailer type is required",
            "weightRequired": "Weight is required"
        }
    },
    "de": {
        "addTrip": "Fahrt hinzufügen",
        "noJourneys": "Keine Fahrten",
        "acknowledgeError": "Fehler quittieren",
        "acknowledgeErrorText": "Bitte bestätigen Sie, dass Sie den Trackingfehler zur Kenntnis genommen haben und allfällige LSV-pflichtigen Fahrten erfasst haben.",
        "comment": "Kommentar",
        "correctionCreated": "Zwischenstrecke erstellt",
        "correctionsCreated": "Zwischenstrecken erstellt",
        "confirm": "Bestätigen",
        "cancel": "Abbrechen",
        "deleteSelected": "Ausgewählte löschen",
        "warning": "Achtung!",
        "confirmTripsRevocation": "Möchten Sie die ausgewählten Fahrten wirklich widerrufen?",
        "errorAcknowledged": "Fehler quittiert",
        "heading": "Bearbeiten",
        "noEnd": "Kein Ende",
        "noTrackingWarningOptional": "Das Signal zum Tracker wurde nicht gefunden. Es wurden keine Fahrten getrackt. Sie können optional Fahrten erfassen.",
        "trailer": "Anhänger",
        "trailerType": "Anhängertyp",
        "noTrailer": "Kein Anhänger",
        "otherTrailer": "Anderer Anhänger",
        "trip": "Fahrt",
        "weight": "Gewicht",
        "tripRevoked": "Fahrt widerrufen",
        "correctionCauses": {
            "CONNECTION_LOST": "Während des gesamten Tages wurden keine Nachrichten empfangen. Deshalb müssen Fahrten oder das Nichtvorhandensein von Fahrten manuell nachgetragen werden.",
            "NOT_INSTALLED": "Das Erfassungsgerät wurde noch nicht in Betrieb genommen. Deshalb müssen Fahrten oder das Nichtvorhandensein von Fahrten manuell nachgetragen werden.",
            "INSTALLATION_DATE": "Das Erfassungsgerät wurde an diesem Tag in Betrieb genommen. Deshalb müssen allfällige Fahrten vor der Inbetriebnahme nachtragen werden.",
            "CONNECTION_LOST_ON_NEXT_DAY": "Das Erfassungsgerät hat während des Tages potenziell die Verbindung verloren. Deshalb müssen allfällige Fahrten manuell nachgetragen werden.",
            "GPS_ISSUE": "Das GPS-Signal war während des Tages teilweise unzuverlässig. Deshalb müssen allenfalls Fahrten manuell überprüft und hinzugefügt werden."
        },
        "errors": {
            "trailerTypeRequired": "Anhängertyp ist erforderlich",
            "weightRequired": "Gewicht ist erforderlich"
        }
    },
    "fr": {
        "noJourneys": "Aucun trajet",
        "heading": "Editer",
        "acknowledgeError": "Reconnaître l'erreur",
        "acknowledgeErrorText": "Veuillez confirmer que vous avez reconnu l'erreur de suivi et avez enregistré les trajets nécessitant la LSV.",
        "comment": "Commentaire",
        "correctionCreated": "Correction créée",
        "correctionsCreated": "Corrections créées",
        "confirm": "Confirmer",
        "cancel": "Annuler",
        "deleteSelected": "Supprimer la sélection",
        "warning": "Attention!",
        "confirmTripsRevocation": "Voulez-vous vraiment révoquer les trajets sélectionnés ?",
        "errorAcknowledged": "Erreur reconnue",
        "noTrackingWarningOptional": "Le signal au tracker n'a pas été trouvé. Aucun trajet n'a été suivi. Vous pouvez enregistrer des trajets en option.",
        "addTrip": "Ajouter un trajet",
        "trailer": "Remorque",
        "trailerType": "Type de remorque",
        "noTrailer": "Pas de remorque",
        "otherTrailer": "Autre remorque",
        "weight": "Poids",
        "trip": "Trajet",
        "noEnd": "Pas de fin",
        "tripRevoked": "Trajet révoqué",
        "correctionCauses": {
            "CONNECTION_LOST": "Aucun message n'a été reçu tout au long",
            "NOT_INSTALLED": "L'appareil d'enregistrement n'a pas encore été mis en service. Les voyages doivent donc être ajoutés manuellement.",
            "INSTALLATION_DATE": "L'appareil d'enregistrement a été mis en service ce jour-là. Par conséquent, les voyages avant le début de l'exploitation doivent être ajoutés manuellement.",
            "CONNECTION_LOST_ON_NEXT_DAY": "L'appareil d'enregistrement a potentiellement perdu la connexion pendant la journée. Par conséquent, les voyages doivent être ajoutés manuellement.",
            "GPS_ISSUE": "Le signal GPS était partiellement peu fiable pendant la journée. Par conséquent, les voyages doivent être vérifiés manuellement et éventuellement ajoutés."
        },
        "errors": {
            "trailerTypeRequired": "Le type de remorque est requis",
            "weightRequired": "Le poids est requis"
        }
    },
    "it": {
        "noJourneys": "Nessun viaggio",
        "heading": "Modificare",
        "acknowledgeError": "Riconosci l'errore",
        "acknowledgeErrorText": "Conferma di aver riconosciuto l'errore di tracciamento e di aver registrato eventuali viaggi che richiedono l'LSV.",
        "comment": "Commento",
        "correctionCreated": "Correzione creata",
        "correctionsCreated": "Correzioni create",
        "confirm": "Conferma",
        "cancel": "Annulla",
        "deleteSelected": "Elimina selezionati",
        "warning": "Attenzione!",
        "confirmTripsRevocation": "Vuoi davvero revocare i viaggi selezionati?",
        "errorAcknowledged": "Errore riconosciuto",
        "noTrackingWarningOptional": "Il segnale al tracker non è stato trovato. Nessun viaggio è stato tracciato. Puoi registrare i viaggi in opzione.",
        "addTrip": "Aggiungi viaggio",
        "trailer": "Rimorchio",
        "trailerType": "Tipo di rimorchio",
        "noTrailer": "Nessun rimorchio",
        "otherTrailer": "Altro rimorchio",
        "weight": "Peso",
        "trip": "Viaggio",
        "noEnd": "Nessuna fine",
        "tripRevoked": "Viaggio revocato",
        "correctionCauses": {
            "CONNECTION_LOST": "Nessun messaggio è stato ricevuto per tutto il giorno. Pertanto, i viaggi devono essere inseriti manualmente.",
            "NOT_INSTALLED": "Il dispositivo di registrazione non è ancora stato messo in funzione. Pertanto, i viaggi devono essere inseriti manualmente.",
            "INSTALLATION_DATE": "Il dispositivo di registrazione è stato messo in funzione in questo giorno. Pertanto, i viaggi prima dell'inizio dell'operazione devono essere inseriti manualmente.",
            "CONNECTION_LOST_ON_NEXT_DAY": "Il dispositivo di registrazione ha potenzialmente perso la connessione durante il giorno. Pertanto, i viaggi devono essere inseriti manualmente.",
            "GPS_ISSUE": "Il segnale GPS era parzialmente inaffidabile durante il giorno. Pertanto, i viaggi devono essere controllati manualmente e possibilmente aggiunti."
        },
        "errors": {
            "trailerTypeRequired": "Il tipo di rimorchio è obbligatorio",
            "weightRequired": "Il peso è obbligatorio"
        }
    }
}
</i18n>

<style lang="scss" scoped>
.lsva-truck-details {
    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        border-bottom: $style-border;
    }

    &__info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 16px;
    }

    &__data {
        h3 ~ * {
            font-size: $fs-small;
        }

        &--accent {
            font-weight: bold;
        }
    }

    &__trips {
        border-top: $style-border;
    }

    &__trips-spinner {
        margin-top: 8px;
    }

    &__trips-head {
        border-bottom: $style-border;
    }

    &__trips-select-all {
        width: 16px;
    }

    &__bulk-edit-form {
        position: sticky;
        bottom: 0;
        background-color: $color-primary-contrast;
        border-top: $style-border;
    }
}
</style>
